"use client";

import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import cn from "classnames";
import { getDataLinkAttributes } from "@/helpers";
import Link from "@/components/Link";
import {
  SecondaryNavigation as SecondaryNavigationMobileProps,
  SecondaryNavigationVariant,
} from "../types";
import { Fragment, useEffect, useRef, useState, FC, useCallback } from "react";
import { Button } from "../../Button";
import { CaretDownLine, CaretDownSolid, Arrow } from "@/icons";

type MenuStateObserverProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const MenuStateObserver: FC<MenuStateObserverProps> = ({
  open,
  onOpenChange,
}) => {
  useEffect(() => {
    onOpenChange(open);
  }, [open, onOpenChange]);

  return null;
};
const MENU_MAX_HEIGHT = "70dvh";
const BOTTOM_FADE_HEIGHT_PX = 56;

const styles: Record<
  SecondaryNavigationVariant,
  {
    container: string;
    productLink: string;
    productLinkText: string;
    menuButtonWrapper: string;
    menuButton: string;
    menuButtonCaret: string;
    menuItemsOpen: string;
    level1Item: string;
    backLink: string;
    level2Container: string;
    categoryButton: string;
    level2Link: string;
    level2RightLink: string;
    level3Container: string;
    level3Link: string;
    level3RightLink: string;
    fade?: string;
  }
> = {
  default: {
    container:
      "bg-swdc-silver-200 flex min-h-13 flex-row flex-wrap items-center justify-between gap-x-8 gap-y-4 border-b-1 border-swdc-gray-200 px-6 py-2",
    productLink:
      "text-link-m text-swdc-black-600 hover:text-swdc-black-600 active:text-swdc-blue-500 mr-auto font-bold -ml-1",
    productLinkText: "text-link-m text-swdc-black-600 mr-auto font-bold",
    menuButtonWrapper: "flex shrink grow items-center justify-between gap-x-8",
    menuButton:
      "group text-link-s sm:text-link-m text-swdc-black-600 active:text-swdc-blue-500 relative flex items-center font-medium outline-none after:absolute after:right-0 after:-bottom-2 after:left-0 after:rounded-full after:bg-transparent after:content-[''] hover:after:bg-swdc-silver-400 focus:after:bg-swdc-silver-400 active:after:bg-swdc-silver-400 hover:after:h-0.5 data-open:after:bg-swdc-silver-400 data-open:after:h-1 data-open:hover:after:h-0.5 data-open:active:after:h-0.5",
    menuButtonCaret: "text-swdc-black-600 group-active:text-swdc-blue-500 ml-2",
    menuItemsOpen: "flex w-full flex-col bg-white",
    level1Item:
      "border-swdc-silver-300 text-link-m relative flex w-full flex-row items-center justify-between border-l-8 py-4 pr-12 pl-4 text-left",
    backLink:
      "border-swdc-silver-200 text-link-s text-swdc-blue-500 flex w-full flex-row items-center gap-x-2 border-b-1 border-l-8 border-solid py-4 pr-6 pl-4",
    level2Container:
      "border-swdc-silver-300 min-h-0 overflow-y-auto border-l-8",
    categoryButton:
      "text-link-m relative flex w-full flex-row items-center justify-between py-4 pr-12 pl-4 text-left",
    level2Link: "text-link-m flex w-full py-4 pr-6 pl-4",
    level2RightLink:
      "text-link-s text-swdc-blue-500 flex w-full py-4 pr-6 pl-4 font-medium underline",
    level3Container: "min-h-0 overflow-y-auto",
    level3Link:
      "border-swdc-silver-300 text-link-m flex w-full border-l-8 border-solid py-4 pr-6 pl-4",
    level3RightLink:
      "border-swdc-silver-300 text-link-s text-swdc-blue-500 mb-20 flex w-full border-l-8 border-solid py-4 pr-6 pl-4 font-medium underline",
  },
  redesigned: {
    container:
      "bg-swdc-cool-gray-200 rounded-xs border-b-swdc-silver-200 flex min-h-13 flex-row items-center justify-between gap-x-4 border-b px-2 py-1",
    productLink:
      "text-link-xs text-swdc-black-600 min-w-0 grow-9999  font-bold capitalize active:scale-[0.98] origin-center transition-transform ease-out duration-100",
    productLinkText:
      "text-link-xs text-swdc-black-600 min-w-0 grow-9999 font-bold capitalize",
    menuButtonWrapper: "flex shrink-0 items-center gap-x-2 whitespace-nowrap",
    menuButton:
      "text-link-xs text-swdc-black-600 data-focus:outline-swdc-focus-primary flex items-center gap-x-0.5 rounded-xs outline-2 outline-offset-0 outline-transparent focus:not-data-focus:outline-none active:scale-[0.96] origin-center transition-transform ease-out duration-100",
    menuButtonCaret: "text-swdc-black-600 ml-0.5",
    menuItemsOpen:
      "bg-swdc-cool-gray-200 rounded-xs flex ml-2 mr-2 flex-col gap-2 px-2 pt-4 pb-2",
    level1Item:
      "text-link-xs text-swdc-black-600 flex w-full flex-row items-center justify-between pt-0.5 pr-0.5 pb-0.5 pl-1 text-left capitalize",
    backLink:
      "border-b-swdc-silver-200 text-link-xs text-swdc-blue-500 flex w-full flex-row items-center gap-x-2 border-b pl-2 pb-2 font-bold underline active:scale-[0.96] origin-center transition-transform ease-out duration-100",
    level2Container: "flex flex-col gap-2",
    categoryButton:
      "text-link-xs text-swdc-black-600 flex w-full flex-row items-center justify-between pt-0.5 pr-0.5 pb-0.5 pl-1 text-left capitalize active:scale-[0.96] origin-center transition-transform ease-out duration-100",
    level2Link:
      "text-body-s text-swdc-black-600 flex w-full px-2 py-2 font-bold active:scale-[0.98] origin-center transition-transform ease-out duration-100",
    level2RightLink:
      "text-link-xs text-swdc-black-600 flex items-center gap-x-1 self-start py-2 px-1 font-bold underline active:scale-[0.98] origin-center transition-transform ease-out duration-100",
    level3Container: "flex flex-col gap-2",
    level3Link:
      "text-body-s text-swdc-black-600 flex w-full p-2 font-bold active:scale-[0.98] origin-center transition-transform ease-out duration-100",
    level3RightLink:
      "text-link-xs text-swdc-black-600 flex items-center w-full p-2 font-bold underline active:scale-[0.98] origin-center transition-transform ease-out duration-100",
    fade: "from-swdc-cool-gray-200 via-swdc-cool-gray-200/80 via-20% to-swdc-cool-gray-200/0 bg-linear-to-t",
  },
};

const SecondaryNavigationMobile: React.FC<SecondaryNavigationMobileProps> = ({
  enableProductLink = true,
  newSecondaryNavigation = false,
  ...props
}) => {
  const variant: SecondaryNavigationVariant = newSecondaryNavigation
    ? "redesigned"
    : "default";
  const isRedesigned = variant === "redesigned";
  const s = styles[variant];
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [subTab, setSubTab] = useState<number | null>(null);
  const navRef = useRef<HTMLDivElement | null>(null);
  const [isStickyTop, setIsStickyTop] = useState<boolean>(
    props.isStickyTop || false,
  );
  const menuItemsRef = useRef<HTMLDivElement | null>(null);
  const [isMenuOverflowing, setIsMenuOverflowing] = useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const heightRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);
  const [menuHeight, setMenuHeight] = useState<number>(0);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0,
  );

  const handleOpenChange = useCallback((open: boolean) => {
    setIsMenuOpen(open);
    if (!open) {
      setActiveTab(null);
      setSubTab(null);
      setIsScrolledToBottom(false);
    }
  }, []);

  useEffect(() => {
    if (navRef.current) {
      const observer = new IntersectionObserver(
        ([e]) => {
          if (e) {
            setIsStickyTop(
              props.isStickyTop ||
                (!e.isIntersecting && e.boundingClientRect.top <= 0),
            );
          }
        },
        { threshold: [1] },
      );
      observer.observe(navRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [props.isStickyTop]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    const updateHeights = () => {
      const heightVal = heightRef.current?.offsetHeight || 0;
      const menuVal = menuRef.current?.offsetHeight || 0;
      setHeight(heightVal);
      setMenuHeight(menuVal);
    };

    const resizeObserver = new ResizeObserver(updateHeights);

    if (heightRef.current) {
      resizeObserver.observe(heightRef.current);
    }
    if (menuRef.current) {
      resizeObserver.observe(menuRef.current);
    }
    updateHeights();

    return () => {
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const el = menuItemsRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setIsMenuOverflowing(el.scrollHeight > el.clientHeight + 1);
      setIsScrolledToBottom(
        el.scrollHeight - el.scrollTop - el.clientHeight <= 1,
      );
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(el);
    el.addEventListener("scroll", checkOverflow, { passive: true });

    return () => {
      resizeObserver.disconnect();
      el.removeEventListener("scroll", checkOverflow);
    };
  }, [activeTab, subTab, props.dropdowns, isMenuOpen]);

  const currentData = activeTab !== null ? props.dropdowns?.[activeTab] : null;
  const currentCategoryLinks =
    currentData?.columns && subTab !== null
      ? currentData.columns[subTab]
      : null;

  const handleCategoryClick = (index: number) => {
    const activeDropdown = props.dropdowns?.[index];
    const isCategoryTab =
      activeDropdown?.columns && activeDropdown.columns.length > 1;
    setActiveTab(index);
    setSubTab(isCategoryTab ? null : 0);
  };

  const handleBackToMain = () => {
    setActiveTab(null);
    setSubTab(null);
  };

  const handleBackToCategories = () => {
    setSubTab(null);
  };

  const isCategoryTab = currentData?.columns && currentData.columns.length > 1;

  const offset = menuHeight + height;
  const isTablet = windowWidth >= 1024;
  const swdcLogoHeight = 76;

  const dynamicHeight = isStickyTop
    ? isTablet
      ? `calc(100vh - ${swdcLogoHeight}px)`
      : `calc(100vh - ${offset}px)`
    : `calc(100vh - ${offset + swdcLogoHeight}px)`;

  const renderChevron = () =>
    isRedesigned ? (
      <CaretDownSolid
        width={10}
        height={10}
        className="text-swdc-black-600 ml-2 shrink-0 -rotate-90"
        aria-hidden="true"
      />
    ) : (
      <Arrow
        className="fill-swdc-gray-400 absolute right-6 h-3 w-[1.488rem]"
        aria-hidden="true"
      />
    );

  const renderBackIcon = () =>
    isRedesigned ? (
      <CaretDownSolid
        width={10}
        height={10}
        fill="#006482"
        className="shrink-0 rotate-90"
        aria-hidden="true"
      />
    ) : (
      <CaretDownLine
        className="size-3 rotate-90"
        fill="#006482"
        aria-hidden="true"
      />
    );

  return (
    <div ref={navRef}>
      <Menu>
        {({ open }) => (
          <>
            <MenuStateObserver open={open} onOpenChange={handleOpenChange} />
            <div>
              <div className={cn("flex flex-col", isRedesigned && "px-2 py-1")}>
                {props.mobileMenuButton && (
                  <div ref={menuRef} className={s.container}>
                    {isRedesigned
                      ? props.productLink?.title &&
                        (enableProductLink && props.productLink.href ? (
                          <Link
                            href={props.productLink.href}
                            className={s.productLink}
                            dld={props.productLink.dld}
                            dlt={props.productLink.dlt}
                          >
                            {props.productLink.title}
                          </Link>
                        ) : (
                          <span className={s.productLinkText}>
                            {props.productLink.title}
                          </span>
                        ))
                      : props.productLink?.href &&
                        props.productLink?.title && (
                          <div className="flex grow-9999 flex-row">
                            {enableProductLink ? (
                              <Button
                                variant="tertiary"
                                iconPosition="left"
                                icon="arrowLeft"
                                href={props.productLink.href}
                                className={s.productLink}
                                dld={props.productLink.dld}
                                dlt={props.productLink.dlt}
                              >
                                {props.productLink.title}
                              </Button>
                            ) : (
                              <span className={s.productLinkText}>
                                {props.productLink.title}
                              </span>
                            )}
                          </div>
                        )}
                    <div className={s.menuButtonWrapper}>
                      {isRedesigned && (
                        <>
                          {isStickyTop && props.rightColumn?.visibleWhenSticky}
                          {props.rightColumn?.alwaysVisible}
                        </>
                      )}
                      <MenuButton
                        className={s.menuButton}
                        {...getDataLinkAttributes({
                          dld: props.mobileMenuButton.dld,
                          dlt: props.mobileMenuButton.dlt,
                        })}
                      >
                        {props.mobileMenuButton.title}
                        <CaretDownSolid
                          width={10}
                          height={10}
                          className={cn(
                            s.menuButtonCaret,
                            "transition-transform duration-100 ease-out",
                            open && "rotate-180",
                          )}
                          aria-hidden="true"
                        />
                      </MenuButton>
                      {!isRedesigned && props.rightColumn?.alwaysVisible}
                    </div>
                  </div>
                )}
              </div>
              <div className={cn(isRedesigned && "relative z-30")}>
                <div className={cn(isRedesigned && "absolute inset-x-0 top-0")}>
                  <MenuItems
                    ref={menuItemsRef}
                    modal={false}
                    transition={isRedesigned}
                    className={cn(
                      isRedesigned
                        ? cn(
                            s.menuItemsOpen,
                            "origin-top overflow-y-auto transition duration-400 ease-out data-closed:-translate-y-2 data-closed:opacity-0 data-leave:duration-200 data-leave:ease-in-out",
                          )
                        : cn(open && s.menuItemsOpen, "text-swdc-black-600"),
                    )}
                    style={
                      isRedesigned
                        ? { maxHeight: MENU_MAX_HEIGHT }
                        : { height: dynamicHeight }
                    }
                  >
                    {activeTab === null ? (
                      props.dropdowns?.map((item, index) =>
                        item.href && item.title ? (
                          <MenuItem key={`${item.title}-${index}`}>
                            <Link
                              href={item.href}
                              className={cn(
                                s.level1Item,
                                isRedesigned &&
                                  "origin-center transition-transform duration-100 ease-out active:scale-[0.98]",
                              )}
                              {...getDataLinkAttributes({
                                dlt: item.dlt,
                                dld: item.dld,
                              })}
                            >
                              {item.title}
                            </Link>
                          </MenuItem>
                        ) : (
                          item.title && (
                            <button
                              key={`${item.title}-${index}`}
                              onClick={() => handleCategoryClick(index)}
                              {...getDataLinkAttributes({
                                dld: item.dld,
                                dlt: item.dlt,
                              })}
                              className={cn(
                                s.level1Item,
                                isRedesigned &&
                                  "origin-center transition-transform duration-100 ease-out active:scale-[0.96]",
                              )}
                            >
                              {item.title}
                              {renderChevron()}
                            </button>
                          )
                        ),
                      )
                    ) : subTab === null && isCategoryTab ? (
                      <>
                        <button
                          {...getDataLinkAttributes(props.mobileBacklink)}
                          onClick={handleBackToMain}
                          className={s.backLink}
                        >
                          {renderBackIcon()}
                          {`${props.mobileBacklink?.backToText} ${props.mobileBacklink?.mainMenuText}`}
                        </button>
                        <div className={s.level2Container}>
                          {!isRedesigned && currentData?.title && (
                            <div className="text-body-m py-2 pr-6 pl-4 font-bold">
                              {currentData?.title}
                            </div>
                          )}
                          {currentData?.columns?.map((column, index) => (
                            <Fragment key={`${column.title}-${index}`}>
                              {column.title && (
                                <button
                                  {...getDataLinkAttributes({
                                    dlt: column.dlt,
                                    dld: column.dld,
                                  })}
                                  onClick={() => setSubTab(index)}
                                  className={s.categoryButton}
                                >
                                  {column.title}
                                  {renderChevron()}
                                </button>
                              )}
                              {!column.title &&
                                column.links?.map(
                                  (link, index) =>
                                    link.title &&
                                    link.href && (
                                      <MenuItem key={`${link.title}-${index}`}>
                                        <Link
                                          {...getDataLinkAttributes({
                                            dlt: link.dlt,
                                            dld: link.dld,
                                          })}
                                          className={s.level2Link}
                                          href={link.href}
                                        >
                                          {link.title}
                                        </Link>
                                      </MenuItem>
                                    ),
                                )}
                            </Fragment>
                          ))}
                          {currentData?.rightLink?.title &&
                            currentData.rightLink.href && (
                              <MenuItem>
                                <Link
                                  className={s.level2RightLink}
                                  href={currentData.rightLink?.href}
                                  {...getDataLinkAttributes({
                                    dlt: currentData.rightLink.dlt,
                                    dld: currentData.rightLink.dld,
                                  })}
                                >
                                  {currentData.rightLink?.title}
                                  {isRedesigned ? (
                                    <CaretDownSolid
                                      width={10}
                                      height={10}
                                      fill="#F37021"
                                      className="-rotate-90"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    ""
                                  )}
                                </Link>
                              </MenuItem>
                            )}
                        </div>
                      </>
                    ) : (
                      <>
                        <button
                          {...getDataLinkAttributes(props.mobileBacklink)}
                          onClick={
                            isCategoryTab
                              ? handleBackToCategories
                              : handleBackToMain
                          }
                          className={s.backLink}
                        >
                          {renderBackIcon()}
                          {isCategoryTab
                            ? `${props.mobileBacklink?.backToText} ${currentData?.title}`
                            : `${props.mobileBacklink?.backToText} ${props.mobileBacklink?.mainMenuText}`}
                        </button>
                        <div className={s.level3Container}>
                          {!isRedesigned && currentCategoryLinks?.title && (
                            <div className="border-swdc-silver-300 text-body-m border-l-8 border-solid py-2 pr-6 pl-4 font-bold">
                              {currentCategoryLinks?.title}
                            </div>
                          )}
                          {currentCategoryLinks?.links?.map(
                            (link, index) =>
                              link.title && (
                                <MenuItem key={`${link.title}-${index}`}>
                                  <Link
                                    {...getDataLinkAttributes({
                                      dlt: link.dlt,
                                      dld: link.dld,
                                    })}
                                    className={cn(
                                      s.level3Link,
                                      !isRedesigned &&
                                        !currentData?.rightLink?.title &&
                                        !currentData?.rightLink?.href &&
                                        "last:mb-20",
                                    )}
                                    href={link.href}
                                  >
                                    {link.title}
                                  </Link>
                                </MenuItem>
                              ),
                          )}
                          {currentData?.rightLink?.title &&
                            currentData.rightLink.href && (
                              <MenuItem>
                                <Link
                                  className={s.level3RightLink}
                                  href={currentData.rightLink?.href}
                                  {...getDataLinkAttributes({
                                    dlt: currentData.rightLink.dlt,
                                    dld: currentData.rightLink.dld,
                                  })}
                                >
                                  {currentData.rightLink?.title}
                                  {isRedesigned ? (
                                    <CaretDownSolid
                                      width={10}
                                      height={10}
                                      fill="#F37021"
                                      className="-rotate-90"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    ""
                                  )}
                                </Link>
                              </MenuItem>
                            )}
                        </div>
                      </>
                    )}
                  </MenuItems>
                  {open &&
                    isRedesigned &&
                    isMenuOverflowing &&
                    !isScrolledToBottom && (
                      <div
                        aria-hidden="true"
                        className={cn(
                          s.fade,
                          "pointer-events-none absolute inset-x-2 bottom-0",
                        )}
                        style={{ height: BOTTOM_FADE_HEIGHT_PX }}
                      />
                    )}
                </div>
              </div>
            </div>
          </>
        )}
      </Menu>
    </div>
  );
};

export default SecondaryNavigationMobile;
